import { ScrollArea } from '@/shared/components/ui/scroll-area'
import React, { useEffect, useState } from 'react'
import { Plus } from 'lucide-react' // Optional icon
import { Separator } from '@/shared/components/ui/separator'
import { Input } from '@/shared/components/ui/input'

interface AddToListBoxProps<T> {
    items: T[]
    valueExtractor: (item: T) => string
    onAdd?: (item: T) => void
    placeholder?: string
    itemRenderer?: (item: T, index: number) => React.ReactNode
    title: string
}
const AddToListBox = <T extends Record<string, any>>({
    items,
    onAdd,
    placeholder,
    valueExtractor,
    title,
}: AddToListBoxProps<T>) => {
    const [inputValue, setInputValue] = useState('')
    const [displayItems, setDisplayItems] = useState<T[]>([])
    const handleAdd = () => {
        const trimmed = inputValue.trim()
        if (!trimmed) return
        const newItem = { id: crypto.randomUUID(), title: trimmed, name: trimmed } as unknown as T
        setDisplayItems([...displayItems, ...[newItem]])
        onAdd?.(newItem)
        setInputValue('')
    }

    useEffect(() => {
        if (items) {
            setDisplayItems(items)
        }
    }, [items])
    return (
        <div className="h-56 flex flex-col overflow-hidden">
            <div className="relative p-2">
                <Input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAdd()
                    }}
                    placeholder={placeholder ?? `Add ${title}`}
                    className="w-full pr-10 px-3 py-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                />
                <button
                    onClick={handleAdd}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-slate-800 rounded-2xl bg-slate-600 p-1"
                    aria-label="Add"
                >
                    <Plus size={18} className="" />
                </button>
            </div>

            <div className="flex-1 overflow-hidden px-4 pb-2">
                <ScrollArea className="h-full">
                    {displayItems && displayItems.length > 0 ? (
                        displayItems.toReversed().map((value, index) => (
                            <div key={index}>
                                <div className="my-1">{valueExtractor(value)}</div>
                                <Separator className="my-2" />
                            </div>
                        ))
                    ) : (
                        <div className="text-muted-foreground text-center py-4">
                            No {title ?? 'items'} added yet.
                        </div>
                    )}
                </ScrollArea>
            </div>
        </div>
    )
}

export default AddToListBox
